'use client';

import React, { useRef, forwardRef, useEffect } from 'react';
import clsx from 'clsx';
import { mutate } from 'swr';
import { CACHE_QUERY_KEY } from 'app/constants';
import { FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { type BasicShopUIPropTypes, type BuyerLayoutProps } from 'shop-ui/types';
import { useMemberContext } from 'app/context/useMember';
import { useRouter } from 'next/navigation';
import { Button, Avatar, Menu, MenuButton, MenuItem, MenuList } from '@shared/ui';
import { userLogout } from 'apis/user';
import { LoginModule, type LoginRef } from '@shared/services';
import { getDynamicImageSource } from 'app/libs/url';
import { useBuyerBrandStore } from 'app/store/brand';
import i18n from '@shared/i18n';
import { headerLoginIconMap, HEADER_LOGIN_ICON_KEY } from './Icon';
import './HeaderLoginIcon.scss';

interface HeaderLoginIconProps extends BasicShopUIPropTypes, BuyerLayoutProps {
    content: string;
}

export const HeaderLoginIcon = forwardRef<HTMLDivElement, HeaderLoginIconProps>(
    (props, ref) => {
        const { style, isEditingMode, styleMap, isBuyer } = props;

        const router = useRouter();

        const loginRef = useRef<LoginRef>(null);

        const { brandMeta = null } = useBuyerBrandStore();

        const {
            state = {
                profile: {
                    id: '',
                    username: '',
                    avatar: {
                        src: '',
                    },
                },
            },
        } = useMemberContext() || {};

        const { profile } = state;

        function handleOnOpen() {
            if (loginRef.current) {
                loginRef.current.onOpen({
                    brandMeta,
                });
            }
        }

        function handleAfterLoginEventCallback() {
            mutate(CACHE_QUERY_KEY.memberProfile);
        }

        async function handleLogout() {
            if (typeof window === 'undefined') {
                return;
            }

            const request = await userLogout();

            if (request) {
                window.location.reload();
            }
        }

        useEffect(() => {
            const element = ref as React.RefObject<HTMLDivElement>;

            if (element?.current && typeof window !== 'undefined' && window) {
                const headerContainer = element.current.closest(
                    '[data-shop-component-header]'
                ) as HTMLElement;

                const cell = element.current.closest(
                    '[data-shop-component-grid-cell]'
                ) as HTMLElement;

                if (headerContainer && cell) {
                    cell.parentElement?.classList.add('header-login-icon--responsive');
                }
            }
        }, [ref]);

        return (
            <div className="header-login-icon" ref={ref} style={style}>
                {!profile.id && (
                    <Button
                        variant="plain"
                        onClick={handleOnOpen}
                        className={clsx({
                            'is-editing': !isBuyer,
                        })}
                        sx={{
                            pointerEvents: isEditingMode ? 'none' : 'auto',
                            color: 'var(--shop-ui-custom-color,rgba(57, 57, 62, 1))',
                            p: '0 !important',
                        }}
                        inline
                    >
                        {
                            headerLoginIconMap[
                                styleMap?.[
                                    FEATURE_FLAG_KEYS.HEADER_ICON_STYLE
                                ] as HEADER_LOGIN_ICON_KEY
                            ]
                        }
                    </Button>
                )}
                {profile.id && (
                    <Menu closeOnSelect={false}>
                        <MenuButton>
                            <Avatar
                                name={profile.username}
                                cursor="pointer"
                                size="sm"
                                {...getDynamicImageSource(profile?.avatar?.src, 100, 100)}
                            />
                        </MenuButton>
                        <MenuList>
                            <MenuItem onClick={() => router.push('/setting')}>
                                {i18n.gettext('會員設定')}
                            </MenuItem>
                            <MenuItem onClick={() => router.push('/order')}>
                                {i18n.gettext('購買訂單')}
                            </MenuItem>
                            <MenuItem onClick={handleLogout}>
                                {i18n.gettext('登出')}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                )}
                <LoginModule
                    ref={loginRef}
                    // eslint-disable-next-line jsx-a11y/aria-role
                    role="member"
                    afterLoginEventCallback={handleAfterLoginEventCallback}
                />
            </div>
        );
    }
);

HeaderLoginIcon.displayName = 'HeaderLoginIcon';
